/**
 * User activity for the idle timeout (from Pay Console): only real input counts — pointer, keyboard, wheel, touch.
 * API traffic does not, so a background request can never keep an idle session alive.
 *
 * The session cookie is shared by every tab, so the idle clock is too: the latest input in any tab is kept in
 * localStorage (a timestamp only), and a tab signs out only when the person has been idle in all of them.
 */

const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
const KEY = 'nest.lastInput';
/** Shared at most this often; the idle clock works in whole seconds and warns minutes ahead. */
const SHARE_EVERY_MS = 5_000;

let lastInputMs = Date.now();
let lastSharedMs = 0;

function share(now: number): void {
  if (now - lastSharedMs < SHARE_EVERY_MS) return;
  lastSharedMs = now;
  try {
    window.localStorage.setItem(KEY, String(now));
  } catch {
    /* storage unavailable: this tab's own clock still applies */
  }
}

function shared(): number {
  try {
    const n = Number(window.localStorage.getItem(KEY));
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** The person's latest input in any tab. */
export function lastUserActivity(): number {
  return Math.max(lastInputMs, shared());
}

/** Explicit activity, e.g. signing in or choosing "Stay signed in". */
export function markUserActivity(): void {
  lastInputMs = Date.now();
  lastSharedMs = 0;
  share(lastInputMs);
}

/** Starts listening for user input; returns the cleanup. */
export function trackUserActivity(): () => void {
  const onInput = () => {
    lastInputMs = Date.now();
    share(lastInputMs);
  };
  for (const e of EVENTS) window.addEventListener(e, onInput, { capture: true, passive: true });
  return () => {
    for (const e of EVENTS) window.removeEventListener(e, onInput, { capture: true });
  };
}
